<template>
    <v-card :loading="loadingProjects || uploading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h3>Delivery Tickets</h3>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text class="pt-4">
            <div
                v-if="loadingProjects"
                class="d-flex justify-center align-center py-10"
            >
                <v-progress-circular indeterminate color="primary" />
            </div>
            <template v-else>
                <v-row class="mx-0 px-3 py-3 grey lighten-4 rounded mb-4">
                    <v-col cols="12" md="4" class="py-1">
                        <span class="font-weight-bold">ITEM CODE:</span>
                        {{ item.itemCode || '-' }}
                    </v-col>
                    <v-col cols="12" md="4" class="py-1">
                        <span class="font-weight-bold">PACKING LIST:</span>
                        {{ item.packingListCode || '-' }}
                    </v-col>
                    <v-col cols="12" md="4" class="py-1">
                        <span class="font-weight-bold">TOTAL SENT:</span>
                        {{ item.qtySentToSite ?? 0 }}
                    </v-col>
                </v-row>

                <v-data-table
                    :headers="headers"
                    :items="tickets"
                    item-key="nameFile"
                    :loading="uploading"
                    class="elevation-0"
                    disable-pagination
                    hide-default-footer
                    :mobile-breakpoint="0"
                    no-data-text="No delivery tickets for this item"
                >
                    <template v-slot:[`item.date`]="{ item: row }">
                        {{ formatDate(row.date) }}
                    </template>
                    <template v-slot:[`item.projectId`]="{ item: row }">
                        {{ getProjectName(row.projectId) }}
                    </template>
                    <template v-slot:[`item.nameFile`]="{ item: row }">
                        <a
                            class="primary--text"
                            @click.prevent="openTicket(row.nameFile)"
                        >
                            {{ row.nameFile }}
                        </a>
                    </template>
                    <template v-slot:[`item.signedNameFile`]="{ item: row }">
                        <a
                            v-if="row.signedNameFile"
                            class="primary--text"
                            @click.prevent="openTicket(row.signedNameFile)"
                        >
                            {{ row.signedNameFile }}
                        </a>
                        <span v-else class="grey--text">Not uploaded</span>
                    </template>
                    <template v-slot:[`item.actions`]="{ item: row }">
                        <div class="d-flex justify-center">
                            <v-file-input
                                v-model="uploadFiles[row.nameFile]"
                                accept="application/pdf"
                                label="Upload signed"
                                dense
                                hide-details
                                hide-input
                                prepend-icon="mdi-file-upload"
                                class="upload-signed-input mt-0 pt-0"
                                @change="onFileSelected(row, $event)"
                            />
                        </div>
                    </template>
                </v-data-table>
            </template>
        </v-card-text>
    </v-card>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'
import moment from 'moment'
import { storage } from '@/services/firebase'
import { loadImage } from '@/helpers/imageHandler.js'

export default {
    name: 'SentToSiteTicketsDialog',
    props: {
        item: {
            type: Object,
            required: true,
        },
        items: {
            type: Array,
            default: () => [],
        },
        companyId: {
            type: String,
            required: true,
        },
        canUploadSigned: {
            type: Boolean,
            default: false,
        },
        projects: {
            type: Array,
            default: () => [],
        },
        loadingProjects: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        uploading: false,
        uploadFiles: {},
        signedNameByTicket: {},
        baseHeaders: [
            {
                text: 'DATE',
                value: 'date',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'projectId',
                align: 'center',
                sortable: false,
            },
            {
                text: 'QTY',
                value: 'qty',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DELIVERY TICKET',
                value: 'nameFile',
                align: 'left',
                sortable: false,
            },
            {
                text: 'SIGNED TICKET',
                value: 'signedNameFile',
                align: 'left',
                sortable: false,
            },
            {
                text: 'UPLOAD SIGNED',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
    }),
    computed: {
        headers() {
            if (this.canUploadSigned) {
                return this.baseHeaders
            }
            return this.baseHeaders.filter(header => header.value !== 'actions')
        },
        tickets() {
            const byFile = {}
            ;(this.item.sentToSite || []).forEach(entry => {
                if (!entry?.nameFile) {
                    return
                }
                const signedNameFile =
                    this.signedNameByTicket[entry.nameFile] ||
                    entry.signedNameFile ||
                    ''
                if (!byFile[entry.nameFile]) {
                    byFile[entry.nameFile] = {
                        nameFile: entry.nameFile,
                        signedNameFile,
                        date: entry.date,
                        qty: Number(entry.qty || 0),
                        projectId: entry.projectId || '',
                    }
                } else {
                    byFile[entry.nameFile].qty += Number(entry.qty || 0)
                    if (signedNameFile) {
                        byFile[entry.nameFile].signedNameFile = signedNameFile
                    }
                    if (!byFile[entry.nameFile].projectId && entry.projectId) {
                        byFile[entry.nameFile].projectId = entry.projectId
                    }
                }
            })
            return Object.values(byFile).sort((a, b) => {
                const dateA = this.toTimestamp(a.date)
                const dateB = this.toTimestamp(b.date)
                return dateB - dateA
            })
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        getProjectName(projectId) {
            if (!projectId) {
                return '-'
            }
            const project = (this.projects || []).find(p => p.id === projectId)
            return project?.label || project?.name || '-'
        },
        toTimestamp(date) {
            if (!date) {
                return 0
            }
            const seconds = date._seconds || date.seconds
            if (seconds) {
                return seconds
            }
            return new Date(date).getTime() / 1000
        },
        formatDate(date) {
            if (!date) {
                return '-'
            }
            const seconds = date._seconds || date.seconds
            if (seconds) {
                return moment.unix(seconds).format('DD/MM/YYYY HH:mm')
            }
            return moment(date).format('DD/MM/YYYY HH:mm')
        },
        getSignedFileName(nameFile) {
            return nameFile.replace(/\.pdf$/i, '_signed.pdf')
        },
        getPackingListIdsForTicket(nameFile) {
            const ids = new Set()
            ;(this.items || []).forEach(row => {
                ;(row.sentToSite || []).forEach(entry => {
                    if (entry?.nameFile === nameFile && row.packingListId) {
                        ids.add(row.packingListId)
                    }
                })
            })
            if (this.item.packingListId) {
                ids.add(this.item.packingListId)
            }
            return [...ids]
        },
        close() {
            this.$emit('closeDialog')
        },
        async openTicket(fileName) {
            if (!fileName) {
                return
            }
            try {
                const url = await loadImage(
                    `${this.companyId}/deliveryTickets`,
                    fileName
                )
                if (url) {
                    window.open(url, '_blank')
                } else {
                    throw new Error('File not found in storage.')
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        onFileSelected(row, file) {
            const selectedFile = Array.isArray(file) ? file[0] : file
            if (!selectedFile) {
                return
            }
            this.uploadSignedTicket(row, selectedFile)
        },
        async uploadSignedTicket(row, file) {
            if (!this.canUploadSigned) {
                return
            }
            try {
                this.uploading = true
                const signedNameFile = this.getSignedFileName(row.nameFile)

                const pdfFile =
                    file instanceof File
                        ? new File([file], signedNameFile, {
                              type: 'application/pdf',
                          })
                        : file

                const storagePath = `${this.companyId}/deliveryTickets`
                const storageRef = storage().ref(storagePath)
                await storageRef.child(signedNameFile).put(pdfFile, {
                    customMetadata: { filename: signedNameFile },
                })

                const packingLists = await API.uploadSignedDeliveryTicket({
                    projectId: this.item.projectId,
                    packingListId: this.item.packingListId,
                    packingListIds: this.getPackingListIdsForTicket(
                        row.nameFile
                    ),
                    nameFile: row.nameFile,
                    signedNameFile,
                })

                this.$set(this.signedNameByTicket, row.nameFile, signedNameFile)
                this.$emit('signedUploaded', {
                    nameFile: row.nameFile,
                    signedNameFile,
                    packingLists,
                })
                this.$set(this.uploadFiles, row.nameFile, null)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.uploading = false
            }
        },
    },
}
</script>

<style scoped>
.upload-signed-input.v-input {
    flex: none;
    width: auto;
    padding-top: 0;
    margin-top: 0;
}

.upload-signed-input >>> .v-input__control {
    display: none;
}

.upload-signed-input >>> .v-input__prepend-outer {
    margin: 0;
}
</style>
